import * as React from 'react';

import {FormField} from 'shells/form/form-field';
import {TextField} from 'shells/textfield/textfield';
import {useDebounce} from 'shells/utils/hooks/use-debounce';
import {useDidMountEffect} from 'shells/utils/hooks/use-did-mount-effect';

import type {EditionFragment as Edition, EditionUpdateInput} from 'nutshell-graphql-types';

import {ABTestedField} from './a-b-tested-field';

type Props = {
    edition: Edition;
    isABTesting?: boolean;
    onEditionSave: (edition: EditionUpdateInput) => Promise<any>;
    showPreviewMode: boolean;
};

export function ABTestComparisonFields(props: Props) {
    const {edition} = props;
    const [editionSubject, setEditionSubject] = React.useState<string>(
        edition.emailTemplate ? edition.emailTemplate.subject : ''
    );
    const debouncedSubject = useDebounce(editionSubject, 300);
    const [alternateSubject, setAlternateSubject] = React.useState<string>(
        (edition.comparisonEdition &&
            edition.comparisonEdition.emailTemplate &&
            edition.comparisonEdition.emailTemplate.subject) ||
            ''
    );
    const debouncedAlternateSubject = useDebounce(alternateSubject, 300);
    const [editionPreviewLine, setEditionPreviewLine] = React.useState<string>(
        edition.emailTemplate ? edition.emailTemplate.previewText : ''
    );
    const debouncedPreviewText = useDebounce(editionPreviewLine, 300);
    const [alternatePreviewText, setAlternatePreviewText] = React.useState<string>(
        (edition.comparisonEdition &&
            edition.comparisonEdition.emailTemplate &&
            edition.comparisonEdition.emailTemplate.previewText) ||
            ''
    );
    const debouncedAlternatePreviewText = useDebounce(alternatePreviewText, 300);

    const [isComparingSubject, setIsComparingSubject] = React.useState<boolean>(
        (edition && edition.comparison && edition.comparison.shouldCompareSubject) || false
    );
    const [isComparingPreviewText, setIsComparingPreviewText] = React.useState<boolean>(
        (edition && edition.comparison && edition.comparison.shouldComparePreviewText) || false
    );

    const handleUpdateEdition = (input: EditionUpdateInput) => {
        props.onEditionSave(input);
    };

    useDidMountEffect(() => {
        handleUpdateEdition({
            editionId: edition.id,
            subject: debouncedSubject.length ? debouncedSubject : null,
            previewText: debouncedPreviewText.length ? debouncedPreviewText : null,
            comparison: {
                shouldComparePreviewText: isComparingPreviewText,
                alternatePreviewText: debouncedAlternatePreviewText.length
                    ? debouncedAlternatePreviewText
                    : null,
                shouldCompareSubject: isComparingSubject,
                alternateSubject: debouncedAlternateSubject.length
                    ? debouncedAlternateSubject
                    : null,
            },
        });
    }, [
        debouncedSubject,
        debouncedPreviewText,
        debouncedAlternatePreviewText,
        debouncedAlternateSubject,
        edition.id,
    ]);

    return props.isABTesting ? (
        <>
            <ABTestedField
                label='Subject line'
                placeholder='No subject'
                primaryText={editionSubject}
                alternateText={alternateSubject}
                onChangePrimary={setEditionSubject}
                onChangeAlternative={setAlternateSubject}
                onToggleIsComparing={() => {
                    handleUpdateEdition({
                        editionId: edition.id,
                        comparison: {
                            shouldCompareSubject: !isComparingSubject,
                        },
                    });
                    setIsComparingSubject(!isComparingSubject);
                }}
                isComparing={isComparingSubject}
                showPreviewMode={props.showPreviewMode}
                edition={edition}
            />
            <ABTestedField
                label='Preview text'
                placeholder='Short blurb about your email'
                primaryText={editionPreviewLine}
                alternateText={alternatePreviewText}
                onChangePrimary={setEditionPreviewLine}
                onChangeAlternative={setAlternatePreviewText}
                onToggleIsComparing={() => {
                    handleUpdateEdition({
                        editionId: edition.id,
                        comparison: {
                            shouldComparePreviewText: !isComparingPreviewText,
                        },
                    });
                    setIsComparingPreviewText(!isComparingPreviewText);
                }}
                helperText='Appears after the subject line in a recipient’s inbox'
                isComparing={isComparingPreviewText}
                showPreviewMode={props.showPreviewMode}
                edition={edition}
            />
        </>
    ) : (
        <>
            <FormField innerId='subject' label='Subject'>
                <TextField
                    id='subject'
                    // @ts-expect-error
                    name='subject'
                    placeholder='No subject'
                    value={editionSubject}
                    onChange={setEditionSubject}
                    disabled={props.showPreviewMode}
                />
            </FormField>
            <FormField
                innerId='previewText'
                label='Preview text'
                helperText='Appears after the subject line in a recipient’s inbox'
            >
                <TextField
                    id='previewText'
                    // @ts-expect-error
                    name='preview text'
                    placeholder='Short blurb about your email'
                    value={editionPreviewLine}
                    onChange={setEditionPreviewLine}
                    disabled={props.showPreviewMode}
                />
            </FormField>
        </>
    );
}
